import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import fde from 'fast-deep-equal';


const _sliceName = 'db';


const adapters = {
    'Session': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'AccountUtilityToken': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'User': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'Edge': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'Thing': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'EnrolledThing': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'ThingInput': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'ThingOutput': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'Home': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'Restriction': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'MonitorHistorySummary': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'MonitorHistory': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'MQTTCertificate': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'MQTTCredential': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'PairingStatus': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'MonitorPreset': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'PublicConfig': createEntityAdapter({
        selectId: (ob) => ob._id,
    }),
    'ZohoAddress': createEntityAdapter({
        selectId: (ob) => ob._id,
    })
}


const DBSlice = createSlice({
    name: _sliceName,
    initialState: {
        Session: adapters.Session.getInitialState(),
        AccountUtilityToken: adapters.AccountUtilityToken.getInitialState(),
        User: adapters.User.getInitialState(),
        Edge: adapters.Edge.getInitialState(),
        Thing: adapters.Thing.getInitialState(),
        EnrolledThing: adapters.EnrolledThing.getInitialState(),
        ThingInput: adapters.ThingInput.getInitialState(),
        ThingOutput: adapters.ThingOutput.getInitialState(),
        Home: adapters.Home.getInitialState(),
        Restriction: adapters.Restriction.getInitialState(),
        MonitorHistorySummary: adapters.MonitorHistorySummary.getInitialState(),
        MonitorHistory: adapters.MonitorHistory.getInitialState(),
        MQTTCertificate: adapters.MQTTCertificate.getInitialState(),
        MQTTCredential: adapters.MQTTCredential.getInitialState(),
        PairingStatus: adapters.PairingStatus.getInitialState(),
        MonitorPreset: adapters.MonitorPreset.getInitialState(),
        PublicConfig: adapters.PublicConfig.getInitialState(),
        ZohoAddress: adapters.ZohoAddress.getInitialState(),
    },
    reducers: {
        updateRelations: (state, action) => {
            const relations = action.payload;
            for (let collectionName in relations) {
                let targetState = state[collectionName];
                let adapter = adapters[collectionName];
                if (adapter) {
                    adapter.setMany(targetState, Object.values(relations[collectionName]));
                } else {
                    console.error("No Adapter for ", collectionName);
                }
            }
        },
        updateItem: (state, action) => {
            const item = action.payload;
            const collectionName = item.collectionName;
            const targetState = state[collectionName];
            const targetAdapter = adapters[collectionName];
            if (!targetState || !targetAdapter) {
                console.error("No Adapter or State for ", collectionName);
            } else {
                targetAdapter.setOne(targetState, item);
            }
        },
        deleteItems: (state, action) => {
            const { collectionName, ids } = action.payload;
            const targetState = state[collectionName];
            const targetAdapter = adapters[collectionName];
            if (!targetState || !targetAdapter) {
                console.error("No Adapter or State for ", collectionName);
            } else {
                targetAdapter.removeMany(targetState, ids);
            }
        },
        updateList: (state, action) => {
            const items = action.payload;
            const map = {};
            items.forEach((item) => {
                let collectionName = item.collectionName;
                if (!map[collectionName]) {
                    map[collectionName] = [];
                }
                map[collectionName].push(item);
            });
            for (let collectionName in map) {
                const targetState = state[collectionName];
                const targetAdapter = adapters[collectionName];
                if (!targetState || !targetAdapter) {
                    console.error("No Adapter or State for ", collectionName);
                } else {
                    targetAdapter.upsertMany(targetState, map[collectionName]);
                }
            }
        }
    }
});



const useDBSelector = (className, id, selector) => {
    return useSelector((state) => {
        let entities = state.db[className].entities || {};
        const ob = entities[id] || {};
        if (typeof selector === 'function') {
            return selector(ob);
        }
        return ob;
    }, (a, b) => {
        if (typeof selector === 'function') {
            return fde(a, b);
        } else {
            return a?._id === b?._id && a?.updatedAt === b?.updatedAt;
        }
    })
}


const useDBListSelector = (className, ids) => {
    return useSelector((state) => {
        let entities = state.db[className].entities;
        return ids.map((id) => {
            return entities[id];
        });
    }, (a, b) => {
        if (a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i += 1) {
            if (a[i].updatedAt !== b[i].updatedAt || a[i]._id !== b[i]._id) {
                return false;
            }
        }
        return true;
    })
}

const useSessionSelector = (id, selector) => useDBSelector('Session', id, selector);
const useAccountUtilityTokenSelector = (id, selector) => useDBSelector('AccountUtilityToken', id, selector);
const useUserSelector = (id, selector) => useDBSelector('User', id, selector);
const useEdgeSelector = (id, selector) => useDBSelector('Edge', id, selector);
const useThingSelector = (id, selector) => useDBSelector('Thing', id, selector);
const useEnrolledThingSelector = (id, selector) => useDBSelector('EnrolledThing', id, selector);
const useThingInputSelector = (id, selector) => useDBSelector('ThingInput', id, selector);
const useThingOutputSelector = (id, selector) => useDBSelector('ThingOutput', id, selector);
const useHomeSelector = (id, selector) => useDBSelector('Home', id, selector);
const useRestrictionSelector = (id, selector) => useDBSelector('Restriction', id, selector);
const useMonitorHistorySummarySelector = (id, selector) => useDBSelector('MonitorHistorySummary', id, selector);
const useMonitorHistorySelector = (id, selector) => useDBSelector('MonitorHistory', id, selector);
const useMQTTCertificateSelector = (id, selector) => useDBSelector('MQTTCertificate', id, selector);
const useMQTTCredentialSelector = (id, selector) => useDBSelector('MQTTCredential', id, selector);
const usePairingStatusSelector = (id, selector) => useDBSelector('PairingStatus', id, selector);
const useMonitorPresetSelector = (id, selector) => useDBSelector('MonitorPreset', id, selector);
const usePublicConfigSelector = (id, selector) => useDBSelector('PublicConfig', id, selector);
const useZohoAddressSelector = (id, selector) => useDBSelector('ZohoAddress', id, selector);


export {
    DBSlice,
    useSessionSelector,
    useAccountUtilityTokenSelector,
    useUserSelector,
    useEdgeSelector,
    useThingSelector,
    useEnrolledThingSelector,
    useThingInputSelector,
    useThingOutputSelector,
    useHomeSelector,
    useRestrictionSelector,
    useMonitorHistorySummarySelector,
    useMonitorHistorySelector,
    useMQTTCertificateSelector,
    useMQTTCredentialSelector,
    usePairingStatusSelector,
    useMonitorPresetSelector,
    usePublicConfigSelector,
    useZohoAddressSelector,
    useDBListSelector,
};